
import { EngineResult } from './constants'
import { decision } from './decision'

// ============================================================
// WEEKLY RECURRENCE EVALUATOR
// ============================================================
//
// Rules:
// 1. One or more weekdays may be selected.
// 2. With no selected weekdays, use the start date's weekday.
// 3. interval_value is the number of weeks between recurrence
//    weeks.
// 4. Multi-week intervals require a recurrence start date.
// 5. Database DATE values are calendar dates, not timestamps.
// ============================================================

function toLocalCalendarDate(value) {
    if (!value) {
        return null
    }

    // Supabase may return a DATE as midnight UTC.
    // Preserve the stored calendar date.
    if (value instanceof Date) {
        if (Number.isNaN(value.getTime())) {
            return null
        }

        return new Date(
            value.getUTCFullYear(),
            value.getUTCMonth(),
            value.getUTCDate()
        )
    }

    if (
        typeof value === 'string' &&
        /^\d{4}-\d{2}-\d{2}$/.test(value)
    ) {
        const [year, month, day] =
            value.split('-').map(Number)

        const result = new Date(year, month - 1, day)

        // Reject invalid calendar dates.
        if (
            result.getFullYear() !== year ||
            result.getMonth() !== month - 1 ||
            result.getDate() !== day
        ) {
            return null
        }

        return result
    }

    const parsed = new Date(value)

    if (Number.isNaN(parsed.getTime())) {
        return null
    }

    return new Date(
        parsed.getUTCFullYear(),
        parsed.getUTCMonth(),
        parsed.getUTCDate()
    )
}

function startOfWeek(date) {
    const result = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate()
    )

    // Sunday-based calendar weeks.
    result.setDate(result.getDate() - result.getDay())

    return result
}

export function evaluateWeekly({
    template,
    operationalDate
}) {
    // ----------------------------------------------------------
    // Operational date
    // ----------------------------------------------------------

    const currentDate =
        operationalDate instanceof Date
            ? (
                Number.isNaN(operationalDate.getTime())
                    ? null
                    : new Date(
                        operationalDate.getFullYear(),
                        operationalDate.getMonth(),
                        operationalDate.getDate()
                    )
            )
            : toLocalCalendarDate(operationalDate)

    if (!currentDate) {
        return decision({
            generate: false,
            code: EngineResult.NOT_READY,
            reason: 'Weekly recurrence has no valid operational date'
        })
    }

    // ----------------------------------------------------------
    // Start date
    // ----------------------------------------------------------

    const startDate =
        toLocalCalendarDate(template.recurrence_start_date)

    // ----------------------------------------------------------
    // End date
    // ----------------------------------------------------------

    const endDate =
        toLocalCalendarDate(template.recurrence_end_date)

    if (endDate && currentDate > endDate) {
        return decision({
            generate: false,
            code: EngineResult.EXPIRED,
            reason: 'Template has expired'
        })
    }

    if (startDate && currentDate < startDate) {
        return decision({
            generate: false,
            code: EngineResult.NOT_STARTED,
            reason: 'Start date has not been reached'
        })
    }

    // ----------------------------------------------------------
    // Weekly interval
    // ----------------------------------------------------------

    const rawInterval = template.interval_value
    const interval =
        rawInterval == null || rawInterval === ''
            ? 1
            : Number(rawInterval)

    if (
        !Number.isInteger(interval) ||
        interval < 1
    ) {
        return decision({
            generate: false,
            code: EngineResult.NOT_READY,
            reason: 'Weekly interval must be a positive integer'
        })
    }

    // Multi-week schedules need a stable anchor.
    // For every-week schedules, the current week is sufficient
    // when no start date exists.
    if (!startDate && interval > 1) {
        return decision({
            generate: false,
            code: EngineResult.NOT_READY,
            reason:
                'Weekly intervals greater than one require a start date'
        })
    }

    // ----------------------------------------------------------
    // Determine scheduled weekdays
    // ----------------------------------------------------------

    let scheduledDays =
        Array.isArray(template.days_of_week)
            ? template.days_of_week
                .map(Number)
                .filter(day =>
                    Number.isInteger(day) &&
                    day >= 0 &&
                    day <= 6
                )
            : []

    // If no weekdays are selected, use the start date's weekday.
    if (scheduledDays.length === 0) {
        if (!startDate) {
            return decision({
                generate: false,
                code: EngineResult.NOT_READY,
                reason:
                    'Weekly recurrence requires a start date or selected weekdays'
            })
        }

        scheduledDays = [startDate.getDay()]
    }

    scheduledDays = [
        ...new Set(scheduledDays)
    ].sort((a, b) => a - b)

    // ----------------------------------------------------------
    // Is today a selected weekday?
    // ----------------------------------------------------------

    if (!scheduledDays.includes(currentDate.getDay())) {
        return decision({
            generate: false,
            code: EngineResult.NOT_READY,
            reason: 'Weekly recurrence is not scheduled today'
        })
    }

    // ----------------------------------------------------------
    // Check the recurrence week against the stable anchor.
    // ----------------------------------------------------------

    const anchorDate = startDate || currentDate
    const anchorWeek = startOfWeek(anchorDate)
    const currentWeek = startOfWeek(currentDate)

    const daysBetween =
        Math.round(
            (currentWeek.getTime() - anchorWeek.getTime()) /
            (24 * 60 * 60 * 1000)
        )

    const weeksSinceStart = Math.floor(daysBetween / 7)

    if (
        weeksSinceStart < 0 ||
        weeksSinceStart % interval !== 0
    ) {
        return decision({
            generate: false,
            code: EngineResult.NOT_READY,
            reason: 'Weekly recurrence interval not reached'
        })
    }

    // ----------------------------------------------------------
    // Generate today's occurrence.
    // ----------------------------------------------------------

    return decision({
        generate: true,
        code: EngineResult.READY,
        reason: 'Weekly recurrence',
        dueDate: currentDate
    })
}
